import { useEffect, useState } from 'react';
import { styled } from '@mui/material/styles';
import LoginBgImg from '@/assets/loginBg.png';
import LoginLogoImg from '@/assets/loginLogo.png';
import { FormattedMessage } from 'react-intl';
import { useAuth0 } from '@auth0/auth0-react';
import { useCookieConsent } from '@/contexts/CookieConsentContext';
import Modal from '@/components/Modal/Modal';
import Button from '@/components/Button/Button';

const LoginPage = () => {
  const { loginWithRedirect } = useAuth0();
  const { hasConsented, acceptAll } = useCookieConsent();
  const [showCookieModal, setShowCookieModal] = useState(false);

  const handleLogin = () => {
    if (!hasConsented) {
      setShowCookieModal(true);
    } else {
      loginWithRedirect();
    }
  };

  const handleAcceptAndLogin = () => {
    acceptAll();
    setShowCookieModal(false);
    loginWithRedirect();
  };

  useEffect(() => {
    if (hasConsented) {
      loginWithRedirect();
    }
  }, [hasConsented]);

  return (
    <>
      <Root>
        {!hasConsented && (
          <LoginModal>
            <ModalHeader>
              <Logo src={LoginLogoImg} alt="Partner Portal Logo" />
              <Header>
                <FormattedMessage
                  id="signInHeader"
                  defaultMessage="Partner Sign In"
                />
              </Header>
              <Subheader>
                <FormattedMessage
                  id="signInSubheader"
                  defaultMessage="To continue to the Partner Portal"
                />
              </Subheader>
            </ModalHeader>
            <ModalBody>
              <StyledButton
                variant="contained"
                fullWidth
                type="submit"
                onClick={handleLogin}
                aria-label="Continue Button"
              >
                <FormattedMessage
                  id="continue"
                  defaultMessage="Continue"
                />
              </StyledButton>
            </ModalBody>
          </LoginModal>
        )}
      </Root>
      <Modal
        open={showCookieModal}
        onClose={() => setShowCookieModal(false)}
        title={
          <FormattedMessage
            id="cookieConsent.loginRequired.title"
            defaultMessage="We value your privacy"
          />
        }
        actions={[
          <Button
            key="cancel"
            variant="text"
            onClick={() => setShowCookieModal(false)}
            aria-label="Cancel Button"
          >
            <FormattedMessage
              id="cookieConsent.cancel"
              defaultMessage="Cancel"
            />
          </Button>,
          <Button
            key="accept"
            variant="contained"
            onClick={handleAcceptAndLogin}
            aria-label="Accept Button"
          >
            <FormattedMessage
              id="cookieConsent.loginRequired.accept"
              defaultMessage="Accept"
            />
          </Button>,
        ]}
      >
        <FormattedMessage
          id="cookieConsent.loginRequired.description"
          defaultMessage="To sign in to your account, we need to use cookies for authentication, security, and to improve your experience."
        />
      </Modal>
    </>
  );
};

const Root = styled('div')({
  position: 'relative',
  width: '100%',
  height: '100%',
  background: `url(${LoginBgImg}) no-repeat center center`,
  backgroundSize: 'cover',
});

const LoginModal = styled('div')(({ theme }) => ({
  position: 'absolute',
  width: '540px',
  left: '50%',
  color: theme.palette.textPrimaryWhite,
  transform: 'translateX(-50%)',
  top: '20%',
  borderRadius: 8,
  overflow: 'hidden',
  boxShadow: '0px 4px 10px 0px #00000066',
}));

const ModalHeader = styled('div')({
  background: '#E6162680',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  padding: '25px 0 26px',
});

const Logo = styled('img')({
  width: 90,
});

const Header = styled('div')({
  fontSize: 24,
  lineHeight: '28px',
  fontWeight: 600,
  marginTop: 9,
});

const Subheader = styled('div')({
  fontSize: 14,
  lineHeight: '20px',
  fontWeight: 400,
  marginTop: 5,
});

const ModalBody = styled('div')({
  background: '#fff',
  padding: '42px 95px',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
});

const StyledButton = styled(Button)({
  boxShadow: 'none',
  textTransform: 'none',
  fontSize: 16,
  lineHeight: '20px',
  padding: '14px 0',
  borderRadius: 8,
});

export default LoginPage;
